import { DOCUMENT } from '@angular/common';
import { RendererFactory2, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ResolveFn } from '@angular/router';
import { Observable, catchError, defaultIfEmpty, of, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import { Product } from '../product/product.types';
import { ProductsService } from '../product/products.service';
import { CanonicalService } from '../shared/canonical.service';

/**
 * SSR resolver for /shop/:category/:subcategory/:product.
 *
 * The product route mounts ProductComponent directly, but ProductComponent sets
 * its SEO inside an async route-params/HTTP subscription. To guarantee the
 * per-product title, og/twitter tags and Product JSON-LD land in the initial
 * SSR HTML response (what crawlers and social-preview bots read), this resolver
 * fetches the product up-front and writes the same payload into <head> before
 * the component renders. ProductComponent re-applies it on the client; its
 * duplicate-prevention guard collapses the two copies into one.
 */
export const productResolver: ResolveFn<Product | null> = (route): Observable<Product | null> => {
  const productsService = inject(ProductsService);
  const canonicalService = inject(CanonicalService);
  const title = inject(Title);
  const meta = inject(Meta);
  const document = inject(DOCUMENT);
  const renderer = inject(RendererFactory2).createRenderer(null, null);

  const seoUrl = `${route.params['category']}/${route.params['subcategory']}/${route.params['product']}`;

  return productsService.getProductByUrlNoSignal(seoUrl).pipe(
    tap((product: Product) => {
      if (!product) return;

      const { baseTitle, baseURL } = environment.config.seo;
      // Title from the product's real name — the URL slug would render as
      // "Farmboxes | Farmboxes | Build-Your-Farmbox-Fruits-Veggies-More".
      const pageTitle = `${product.name} | ${baseTitle}`;
      title.setTitle(pageTitle);
      meta.updateTag({ property: 'og:title', content: pageTitle });
      meta.updateTag({ name: 'twitter:title', content: pageTitle });
      meta.updateTag({ property: 'og:url', content: `${baseURL}shop/${seoUrl}` });
      meta.updateTag({ name: 'twitter:url', content: `${baseURL}shop/${seoUrl}` });
      canonicalService.setPageType('product');

      const description = (product.description?.short || product.teaserDescription || '')
        .replace(/<[^>]*>/g, '').substring(0, 160);
      if (description) canonicalService.setPageDescription(description);

      const imageUrl = (product as any).hrImg || product.img;
      if (imageUrl) canonicalService.setPageImage(imageUrl);

      document.head
        .querySelectorAll('script[type="application/ld+json"]')
        .forEach(s => {
          const text = s.textContent || '';
          if (text.includes('"@type":"Product"') || text.includes('"@type": "Product"')) {
            renderer.removeChild(document.head, s);
          }
        });

      const productUrl = `${baseURL}shop/${seoUrl}`;

      // `priceValidUntil` tells Google how long to trust the price. It only means
      // anything while a sale is running, and a stale or invented date makes Google
      // drop the price from the rich result — so it is emitted only when the product
      // actually carries a sale end date. The API returns it at product level; the
      // typings only declare it per package, hence the cast plus the package fallback.
      const saleEndsAt = (product as any).salePriceEndDate
        || (product as any).packages?.find((p: any) => p.isOnSale)?.salePriceEndDate
        || null;

      const data: any = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: product.name,
        sku: String(product.id),
        url: productUrl,
        brand: { '@type': 'Brand', name: product.producer?.name || environment.application.name },
        offers: {
          '@type': 'Offer',
          price: String(product.price ?? ''),
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          url: productUrl,
          ...(saleEndsAt ? { priceValidUntil: saleEndsAt } : {}),
          seller: { '@type': 'Organization', name: environment.application.name }
        }
      };
      if (product.description?.short) {
        data.description = product.description.short.replace(/<[^>]*>/g, '').substring(0, 5000);
      }
      if (imageUrl) data.image = canonicalService.getAbsoluteImageUrl(imageUrl);
      if (product.category?.name) data.category = product.category.name;

      const script = renderer.createElement('script') as HTMLScriptElement;
      script.type = 'application/ld+json';
      script.text = JSON.stringify(data);
      renderer.appendChild(document.head, script);
    }),
    defaultIfEmpty(null),
    catchError(() => of(null))
  );
};
